import each from 'lodash/each';
import has from 'lodash/has';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import { createSelector } from 'reselect';
import {
  GUIDECHANNELS_LAYOUT,
  LOCAL_STORAGE_LAST_CHANNEL_SEEN,
  OLD_EXPERIENCE,
  GetRegion,
  UNKNOWN_LAYOUT,
} from '../../constants';
import ClaroUrlBuilder from '../../utils/ClaroUrlBuilder';
import Utils from '../../utils/nav/Utils';

function isOldExperience(nodes) {
  return nodes.every(node => node.app_behaviour === null);
}

export function getSafeAppbehaviour(appBehaviour) {
  try {
    return (appBehaviour && JSON.parse(appBehaviour)) || {};
  } catch (err) {
    return {};
  }
}

function enrichNavData(
  navItem,
  layoutNodeConfig,
  flagOldExperience,
  defaultChannel,
  assets,
) {
  const __item = navItem;
  const hasChilds = (__item.childs && __item.childs.length > 0) || false;
  const appBehaviour = getSafeAppbehaviour(__item.app_behaviour);
  const navImage =
    appBehaviour.layout === 'claromusica'
      ? assets.claro_musica_logo
      : assets[`ic_imgmenu_${navItem.code}`] || false;
  const navIcon = assets[`ic_menu_${navItem.code}`] || false;
  const menuType = appBehaviour.menu_type;
  const showTitle = has(appBehaviour, 'node_config.show_title')
    ? appBehaviour.node_config.show_title
    : false;
  const showNode = has(appBehaviour, 'node_config.show_node')
    ? appBehaviour.node_config.show_node
    : false;

  let layout = appBehaviour.layout || UNKNOWN_LAYOUT;

  if (layout === UNKNOWN_LAYOUT && layoutNodeConfig) {
    const layoutConfig =
      layoutNodeConfig[GetRegion()] || layoutNodeConfig.default;
    if (layoutConfig[navItem.code]) {
      layout = layoutConfig[navItem.code].layout;
    }
  }

  if (flagOldExperience && layout === UNKNOWN_LAYOUT && hasChilds) {
    layout = OLD_EXPERIENCE;
  }

  if (hasChilds) {
    __item.childs = __item.childs.map(child => ({
      ...enrichNavData(child, layoutNodeConfig, false, defaultChannel, assets),
      parent: __item,
    }));
  }
  if (layout === GUIDECHANNELS_LAYOUT) {
    __item.shouldRedirect = true;
    const lastChannelSeen = localStorage.getItem(
      LOCAL_STORAGE_LAST_CHANNEL_SEEN,
    );

    if (GetRegion() === 'argentina') {
      __item.urlRedirect = ClaroUrlBuilder.player('775332');
      __item.lastChannelGroupId = '775332';
    } else if (!isNil(lastChannelSeen)) {
      __item.urlRedirect = ClaroUrlBuilder.player(lastChannelSeen);
      __item.lastChannelGroupId = lastChannelSeen;
    } else {
      __item.urlRedirect = ClaroUrlBuilder.player(defaultChannel);
      __item.lastChannelGroupId = defaultChannel;
    }
  }

  return {
    ...__item,
    layout,
    hasChilds,
    navImage,
    navIcon,
    menuType,
    showNode,
    showTitle,
    parent: null,
  };
}

function setNavVisibility(node, navVisibility) {
  const nav_visibility = Utils.nav_visibilityExists(navVisibility);
  if (nav_visibility) {
    return Object.assign({}, node, Utils.addProperties(node, nav_visibility));
  }

  return node;
}

function findCodeFromHistory(path, nodes) {
  let code = null;
  const nodePath = path.split('/');
  const codePath = nodePath[2] || null;
  each(nodes, node => {
    if (node.hasChilds) {
      const nodesByCode = keyBy(node.childs, 'code');
      each(nodesByCode, nodeChild => {
        if (codePath == nodeChild.code) {
          code = nodeChild.code;
        }
      });
    }
    if (codePath == node.code) {
      code = node.code;
    }
  });
  return code;
}

function findNestedNodeByCode(nodeCode, nodes) {
  let nodeFinded = null;
  each(nodes, node => {
    if (node.hasChilds) {
      const nodesByCode = keyBy(node.childs, 'code');
      if (typeof nodesByCode[nodeCode] !== 'undefined') {
        nodeFinded = nodesByCode[nodeCode];
      }
    }
  });

  return nodeFinded;
}

const getDefaultSChannel = state =>
  JSON.parse(state.metadata.default_group_id_channel || '{}');
const getRegion = state => state.user.region;
const getNavVisibility = state => state.metadata.nav_visibility;
const getNodes = state => state.nav.nodeList;
const getNodeSelected = state => state.nav.current;
const getHighlights = state => state.nav.highlights;
const getLayoutNodeConfig = state =>
  JSON.parse(state.metadata.layout_node_configuration);
const getRouting = state => state.routing;
const getAssets = state => state.asset;

export const isOldExpSelector = createSelector(
  getNodes,
  isOldExperience,
);
export const getNodesWOHome = createSelector(
  [getNodes],
  nodes => nodes.filter(node => node.code !== 'homeuser'),
);
export const isOLDExperience = createSelector(
  getNodesWOHome,
  isOldExperience,
);

const getDefaultChannel = createSelector(
  getDefaultSChannel,
  getRegion,
  (defaultsChannel, region) =>
    defaultsChannel[region] || defaultsChannel.default,
);

export const getNodesEnriched = createSelector(
  getNodes,
  getNavVisibility,
  getLayoutNodeConfig,
  isOldExpSelector,
  getDefaultChannel,
  getAssets,
  (
    nodes,
    navVisibility,
    layoutNodeConfig,
    flagOldExperience,
    defaultChannel,
    assets,
  ) =>
    nodes.map(node => ({
      ...setNavVisibility(
        enrichNavData(
          node,
          layoutNodeConfig,
          flagOldExperience,
          defaultChannel,
          assets,
        ),
        navVisibility,
      ),
    })),
);
export const getNodesWithHighlighs = createSelector(
  getNodesEnriched,
  getHighlights,
  (nodes, highlights) =>
    map(nodes, node => {
      const nodeHighlights = highlights[node.code];
      return {
        ...node,
        hasHighlight: !!nodeHighlights,
        highlights: nodeHighlights || [],
      };
    }),
);
export const getNodesByCode = createSelector(
  getNodesEnriched,
  nodes => keyBy(nodes, 'code'),
);
export const getCurrentNode = createSelector(
  getNodeSelected,
  getNodesEnriched,
  getNodesByCode,
  getRouting,
  (nodeCode, nodes, nodesByCode) => {
    const path = location.pathname;
    let node = nodeCode;

    if (!path.indexOf(node) > -1) {
      node = findCodeFromHistory(path, nodes);
    }

    if (node !== null && typeof nodesByCode[node] === 'undefined') {
      return findNestedNodeByCode(node, nodes);
    }
    return nodesByCode[node];
  },
);

export const getNodeByChildCode = (nodes, code) => {
  let foundNode = {};

  nodes.forEach(node => {
    // Con Hijos
    if (node.childs && node.childs.length > 0) {
      if (node.code === code) {
        foundNode = {
          nodes: node.childs,
          selected: null,
          layout: null,
          text: node.text,
          config: JSON.parse(node.app_behaviour),
          self: node,
        };
      }
      node.childs.forEach(sonNode => {
        if (sonNode.code == code) {
          foundNode = {
            nodes: node.childs,
            selected: sonNode.code,
            layout: sonNode.layout,
            text: sonNode.text,
            config: JSON.parse(node.app_behaviour),
            self: node,
          };
        }

        if (!Object.keys(foundNode).length) {
          if (sonNode.childs && node.childs.length > 0) {
            sonNode.childs.forEach(grandChildNode => {
              if (grandChildNode.code == code) {
                foundNode = {
                  nodes: node.childs,
                  selected: sonNode.code,
                  layout: sonNode.layout,
                  text: sonNode.text,
                  config: JSON.parse(node.app_behaviour),
                  self: node,
                };
              }
            });
          }
        }
      });
    } else {
      // SIN hijos
      if (node.code === code) {
        foundNode = {
          nodes: null,
          selected: null,
          layout: node.layout,
          text: node.text,
          config: JSON.parse(node.app_behaviour),
          self: node,
        };
      }
    }
  });
  return foundNode;
};
